import { useContext, useCallback, useEffect, useRef, useState } from 'react'
import AssignTicketsBulkModal from './AssignTicketsBulkModal'
import { useNavigate, useParams } from 'react-router-dom'

import EventDetailsContext from '../../context/EventDetails/EventDetails'
import UserContext from '../../context/User/User'
import { usePagination } from '../../providers/PaginationProvider/PaginationProvider'
import { useModal } from '../../providers/ModalProvider/ModalProvider'
import { AssignTicketType } from '../../providers/AssignToProvider/AssignTicketType'

import AuthService from '../../utilities/services/auth.service'

import { unifiedSearch } from '../../utilities/api'

import useDebouncedValue from '../../utilities/useDebouncedValue'
import useAbortableFetch from '../../utilities/useAbortableFetch'

import { ORGANIZATION_PERMISSION_SLUGS } from '@blocktickets/shared-utils'
import { checkPermission, paymentStatuses } from '../../utilities/helpers'

import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'

import { SearchHeader } from './SearchHeader'
import { NoPermissionsContainer } from '../NoPermissionsContainer'
import { PageLoadingContainer } from '../PageLoadingContainer'
import { LoadingContainer } from '../LoadingContainer'
import { EmptyContainer } from '../EmptyContainer'
import { Pagination } from '../Pagination'
import { EventAssignTable } from './Results/EventAssignTable'

const statusOpts = [
  {
    label: 'All',
    value: 'all',
  },
  ...[
    ...new Map(
      Object.values(paymentStatuses).map((obj) => [JSON.stringify(obj), obj])
    ).values(),
  ],
]

export default function UnifiedSearchList({ uuid }) {
  const { openModal } = useModal()
  const [orders, setOrders] = useState([])
  const [isInitialState, setIsInitialState] = useState(true)
  const [isLoading, setIsLoading] = useState(true)
  const [status, setStatus] = useState(statusOpts[0].value)
  const [searchTerm, setSearchTerm] = useState('')
  const didMountRef = useRef(false)
  const organization = AuthService.getOrg()
  const [showBulkReservedModal, setShowBulkReservedModal] = useState(false)

  // Hooks for convenience
  const debouncedQuery = useDebouncedValue(searchTerm, 300)
  const abortable = useAbortableFetch()

  const {
    currentPage,
    setCurrentPage,
    perPage,
    pageCount,
    setPageCount,
    resultsPerPageOpts,
    handleChange,
  } = usePagination()

  const { hasEventEnded } = useContext(EventDetailsContext)
  const { orgPermissions } = useContext(UserContext)
  const { getPermissions } = AuthService

  const orgPermissionsReady = orgPermissions?.length > 0
  const hasPermission =
    orgPermissionsReady &&
    checkPermission(
      getPermissions(),
      ORGANIZATION_PERMISSION_SLUGS.EVENT_MANAGEMENT
    )

  const runSearch = useCallback(
    async (isFirstTime = false) => {
      setIsLoading(true)
      try {
        const res = await abortable((signal) =>
          unifiedSearch(
            {
              organizationUUID: organization?.[0]?.uuid,
              uuid: uuid,
              by: 'event',
              sourceType: 'ticket_assignment',
              searchTerm: debouncedQuery,
              paymentStatus: status,
              page: currentPage + 1,
              perPage: perPage,
            },
            signal
          )
        )

        const {
          data,
          meta: {
            pagination: { pageCount: pc },
          },
        } = res.data

        setOrders(data)
        setPageCount(pc)
        setIsLoading(false)
      } catch (err) {
        if (err?.name !== 'CanceledError') {
          console.error(err)
          setIsLoading(false)
        }
      } finally {
        setIsInitialState(isFirstTime)
        setIsLoading(false)
      }
    },
    [
      abortable,
      uuid,
      currentPage,
      perPage,
      debouncedQuery,
      status,
      setPageCount,
    ]
  )

  const handleStatusChange = (value) => {
    setStatus(value)
  }

  const openTicketTypeModal = () => {
    openModal(
      AssignTicketType,
      {
        eventId: uuid,
      },
      { className: 'modal-lg' }
    )
  }

  useEffect(() => {
    if (!didMountRef.current) return // ⛔ skip on first render
    runSearch() // ✅ run only for real changes
  }, [currentPage, perPage])

  useEffect(() => {
    if (currentPage === 0 && didMountRef.current) {
      runSearch()
      return
    }

    setCurrentPage(0)
  }, [debouncedQuery, status])

  useEffect(() => {
    if (!orgPermissionsReady || !hasPermission) return
    runSearch(true) // True as in it is initial query
    didMountRef.current = true
    // run once when org permissions become ready
  }, [orgPermissionsReady, hasPermission])

  if (!orgPermissionsReady) {
    return <PageLoadingContainer />
  }

  if (!hasPermission) {
    return <NoPermissionsContainer />
  }

  return (
    <>
      <div className="position-relative">
        <section
          data-testid="unified-search-list-section"
          className="max-width-wrapper"
        >
          <Header
            isInitialState={isInitialState}
            orders={orders}
            organization={organization[0]}
            status={status}
            handleStatusChange={handleStatusChange}
            setSearchTerm={setSearchTerm}
            openTicketTypeModal={openTicketTypeModal}
            hasEventEnded={hasEventEnded}
            onOpenBulkReserved={() => setShowBulkReservedModal(true)}
          />
          <Results
            orders={orders}
            setOrders={setOrders}
            isInitialState={isInitialState}
            isLoading={isLoading}
            pagination={{
              currentPage,
              setCurrentPage,
              perPage,
              pageCount,
              setPageCount,
              resultsPerPageOpts,
              handleChange,
            }}
            runSearch={runSearch}
          />
        </section>
      </div>
      <AssignTicketsBulkModal
        eventUUID={uuid}
        show={showBulkReservedModal}
        onHide={() => setShowBulkReservedModal(false)}
        disabled={hasEventEnded}
        onCompleted={runSearch}
      />
    </>
  )
}

function Results({
  orders,
  setOrders,
  isInitialState,
  isLoading,
  pagination,
  runSearch,
}) {
  if (isLoading) {
    return <LoadingContainer />
  }

  // InitialState
  if (isInitialState && !orders?.length) {
    return (
      <Card body>
        <EmptyContainer style="center lg">
          <p>No ticket orders</p>
          <p className="subtitle">
            Use Import from Excel or Pick seats on map above to add your first
            ticket order.
          </p>
        </EmptyContainer>
      </Card>
    )
  }

  if (orders?.length === 0) {
    return (
      <Card body>
        <EmptyContainer style="center lg">
          <p>No results found...</p>
        </EmptyContainer>
      </Card>
    )
  }

  return (
    <>
      <EventAssignTable
        orders={orders}
        setOrders={setOrders}
        runSearch={runSearch}
      />
      <Pagination
        pageCount={pagination.pageCount}
        perPage={pagination.perPage}
        currentPage={pagination.currentPage}
        resultsPerPageOpts={pagination.resultsPerPageOpts}
        handleClick={(value) => pagination.setCurrentPage(value.selected)}
        handleChange={pagination.handleChange}
      />
    </>
  )
}

function Header({
  isInitialState,
  orders,
  organization,
  status,
  handleStatusChange,
  setSearchTerm,
  openTicketTypeModal,
  hasEventEnded,
  onOpenBulkReserved,
}) {
  const navigate = useNavigate()
  const { uuid } = useParams()
  const assignDisabled = hasEventEnded

  return (
    <SearchHeader
      selectOptions={{
        show: true,
        options: statusOpts,
        value: status,
        handleChange: handleStatusChange,
        label: 'Filter By Status',
        labelId: 'filter-by-status',
      }}
      showActions={!isInitialState || orders?.length > 0}
      rightSideComponent={
        <div className="d-flex flex-column align-items-end gap-2">
          <span className="text-muted small text-end lh-sm mb-0">
            Two ways to assign tickets
          </span>
          <div className="d-flex flex-wrap gap-2 justify-content-end align-items-center">
            <Button
              variant="outline-light"
              size="lg"
              className="btn-plus"
              disabled={assignDisabled}
              onClick={onOpenBulkReserved}
              aria-label="Import reserved ticket assignments from an Excel spreadsheet"
            >
              Import from Excel
            </Button>
            <Button
              variant="outline-light"
              size="lg"
              className="btn-plus"
              disabled={assignDisabled}
              onClick={() => navigate(`/event/${uuid}/seatmap/assign`)}
              aria-label="Open the seat map to choose seats for ticket assignments"
            >
              Pick seats on map
            </Button>
          </div>
        </div>
      }
      title="Assign tickets"
      desc="Import many assignments from a spreadsheet, or open the seat map to choose seats for each fan."
      searchPlaceholder="Search by email"
      setSearchTerm={setSearchTerm}
      data={orders}
      exportedData="invoices"
      eventName={orders[0]?.event?.name}
      organizationName={organization.name}
    />
  )
}
